import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  MenuItem,
  TextField,
  Select,
  FormControl,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import { useAuth } from '../context/AuthContext';
import { userService, User, UserCreate, UserUpdate } from '../services/userService';
import { userPrivilegeService, UserPrivilege } from '../services/userPrivilegeService';
import { meetingTypeService, MeetingType } from '../services/meetingTypeService';
import { clinicService, Clinic } from '../services/clinicService';
import patientTypeService, { PatientType } from '../services/patientTypeService';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { userColumns } from '../config/userColumns';
import { formatDate } from '../utils/dateUtils';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { getApiError } from '../utils/apiError';

const textFieldInputProps = { style: CLINIC_DIALOG_SX.textField as React.CSSProperties };

const selectSx = (hasValue: boolean) => ({
  ...CLINIC_DIALOG_SX.select,
  color: hasValue ? CLINIC_DIALOG_SX.fieldLabel.color : '#999',
});

/** Plain words for what the two clinic lists add up to.
 *
 * The rules narrow rather than enumerate, which reads oddly the first time:
 * both lists empty means every clinic, and an inclusion makes the list
 * exhaustive. Saying the outcome out loud costs one line and saves an admin
 * guessing whether they have just restricted someone to nothing.
 */
function clinicScopeSummary(
  mode: ClinicMode,
  picked: number[],
  clinics: Clinic[],
): string {
  const nameOf = (id: number) => clinics.find((c) => c.id === id)?.name || `#${id}`;
  if (mode === 'all') {
    return 'Works at every clinic, including any added later.';
  }
  if (picked.length === 0) {
    // Saving now would store nothing, which reads back as "all clinics" —
    // the opposite of what the mode says.
    return mode === 'include'
      ? 'Pick at least one clinic, or this saves as every clinic.'
      : 'Nothing excluded yet — this saves as every clinic.';
  }
  const names = picked.map(nameOf).join(', ');
  if (mode === 'include') return `Works at ${names} only.`;
  const left = clinics.filter((c) => !picked.includes(c.id)).length;
  return `Works at every clinic except ${names} — ${left} clinic(s).`;
}

/** Keeps a long option list inside the viewport.
 *
 * The clinic list is twenty-odd entries and ran off the bottom of the screen,
 * so the last few could not be reached without scrolling the page behind the
 * dialog.
 */
/** How a therapist's clinics are scoped.
 *
 *   all      no rows: every clinic in the tenant, including ones added later
 *   include  exactly the chosen clinics
 *   exclude  every clinic except the chosen ones
 *
 * Asking it as one question stops the two lists being filled at once, which
 * the rules resolve but nobody can read off the screen.
 */
type ClinicMode = 'all' | 'include' | 'exclude';

const CLINIC_MODE_LABEL: Record<ClinicMode, string> = {
  all: 'All clinics',
  include: 'Only the selected clinics',
  exclude: 'All clinics except the selected',
};

/** The stored pair as the single mode the form shows. */
const clinicModeOf = (included?: number[], excluded?: number[]): ClinicMode => {
  if ((included || []).length) return 'include';
  if ((excluded || []).length) return 'exclude';
  return 'all';
};

const MULTI_SELECT_MENU_PROPS = {
  PaperProps: { style: { maxHeight: 320 } },
} as const;

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Entity-specific state
  const [privileges, setPrivileges] = useState<UserPrivilege[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [patientTypes, setPatientTypes] = useState<PatientType[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [pendingSignatureFile, setPendingSignatureFile] = useState<File | null>(null);
  const [pendingSignaturePreview, setPendingSignaturePreview] = useState<string | null>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, reset, watch, control, setValue, formState: { errors } } = useForm<UserCreate & UserUpdate & { confirmPassword?: string }>();
  // Opened programmatically when the scope mode changes, so the list that
  // now needs filling is in front of the admin rather than one click away.
  const [clinicListOpen, setClinicListOpen] = useState(false);
  const watchedClinicMode = (watch('clinic_mode' as any) || 'all') as ClinicMode;
  const watchedClinicIds = (watch('clinic_ids' as any) || []) as number[];

  // Helper functions
  const getPrivilegeName = (privilegeId: number | null): string => {
    if (!privilegeId) return 'None';
    const privilege = privileges.find(p => p.id === privilegeId);
    return privilege ? privilege.name : `Privilege ${privilegeId}`;
  };

  const formatUserType = (userType: string | null): string => {
    if (!userType) return 'N/A';
    return userType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getSortValue = useCallback((user: User, key: string): any => {
    switch (key) {
      case 'name': return user.full_name.toLowerCase();
      case 'email': return user.email.toLowerCase();
      case 'userType': return (user.user_type || '').toLowerCase();
      case 'privilege': return getPrivilegeName(user.privilege_id).toLowerCase();
      case 'phone': return user.phone || '';
      case 'status': return user.is_active ? 'active' : 'inactive';
      case 'twoFactor': return user.is_2fa_enabled ? 'enabled' : 'disabled';
      case 'subClinic': return (user.sub_clinic || '').toLowerCase();
      case 'createdAt': return user.created_at;
      default: return '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privileges]);

  const getSearchText = useCallback((user: User): string[] => {
    return [user.full_name, user.email];
  }, []);

  // Sub clinic is an HMO-only field: shown for users whose own privilege has hmo
  const privilegeHasHmo = useCallback(
    (privilegeId: number | null | undefined): boolean =>
      !!privileges.find(p => p.id === privilegeId)?.hmo,
    [privileges]
  );

  const table = useTablePage<User>({
    entityType: 'users',
    columns: userColumns,
    tenantId: currentUser?.tenant_id,
    getSortValue,
    getSearchText,
  });

  // Load data
  useEffect(() => {
    const loadUsersData = async () => {
      try {
        table.setLoading(true);
        table.setError(null);

        const [usersData, privilegesData, meetingTypesData, clinicsData, patientTypesData] = await Promise.all([
          userService.getUsers(),
          userPrivilegeService.getUserPrivileges(),
          meetingTypeService.getMeetingTypes(currentUser?.tenant_id ?? undefined),
          // Both lists are small and only used by the form. Failing to load
          // either must not take the whole page down with it — the user list
          // is still worth showing.
          clinicService.getClinics(currentUser?.tenant_id ?? undefined).catch(() => []),
          currentUser?.tenant_id
            ? patientTypeService.getAll(currentUser.tenant_id).catch(() => [])
            : Promise.resolve([]),
        ]);

        table.setData(usersData);
        setPrivileges(privilegesData);
        setMeetingTypes(meetingTypesData);
        setClinics(clinicsData as Clinic[]);
        setPatientTypes(patientTypesData as PatientType[]);
      } catch (err) {
        console.error('Error loading users data:', err);
        table.setError(err instanceof Error ? err.message : 'Failed to load users data');
      } finally {
        table.setLoading(false);
      }
    };

    if (currentUser?.user_privilege?.manage_users) {
      loadUsersData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Entity-specific handlers
  const handleAddUser = () => {
    setEditingUser(null);
    reset({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'admin',
      user_type: undefined,
      privilege_id: undefined,
      phone: '',
      license_number: '',
      yoman_id: '',
      sub_clinic: '',
      timezone: 'Asia/Jerusalem',
      data_access: 'PROD',
      is_active: true,
      meeting_type_ids: [],
      // Empty means Adult only for patient types, and ALL clinics for the two
      // clinic lists. The defaults differ on purpose — see userService.ts.
      patient_type_ids: [],
      clinic_mode: 'all',
      clinic_ids: [],
      accepts_new_patients: true,
    } as any);
    setSignatureUrl(null);
    setPendingSignatureFile(null);
    setPendingSignaturePreview(null);
    // A menu left open from the last edit must not reappear here.
    setClinicListOpen(false);
    setAddDialogOpen(true);
  };

  const handleNameClick = (user: User) => {
    setEditingUser(user);
    setSignatureUrl(null);
    setPendingSignatureFile(null);
    setPendingSignaturePreview(null);
    reset({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      user_type: user.user_type || undefined,
      privilege_id: user.privilege_id || undefined,
      phone: user.phone || '',
      license_number: user.license_number || '',
      yoman_id: user.yoman_id || '',
      sub_clinic: user.sub_clinic || '',
      timezone: user.timezone || 'Asia/Jerusalem',
      data_access: (user as any).data_access || 'PROD',
      is_active: user.is_active,
      meeting_type_ids: user.meeting_type_ids || [],
      patient_type_ids: user.patient_type_ids || [],
      clinic_mode: clinicModeOf(user.included_clinic_ids, user.excluded_clinic_ids),
      clinic_ids: (user.included_clinic_ids || []).length
        ? user.included_clinic_ids
        : (user.excluded_clinic_ids || []),
      accepts_new_patients: user.accepts_new_patients ?? true,
    } as any);
    if (user.user_type === 'doctor' && user.signature_s3_key) {
      setSignatureLoading(true);
      userService.getSignatureUrl(user.id)
        .then(url => setSignatureUrl(url))
        .catch(() => {})
        .finally(() => setSignatureLoading(false));
    }
    // A menu left open from the last edit must not reappear here.
    setClinicListOpen(false);
    setAddDialogOpen(true);
  };

  const handleSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!editingUser) {
      // Create mode: store file and show local preview
      setPendingSignatureFile(file);
      setPendingSignaturePreview(URL.createObjectURL(file));
      if (signatureInputRef.current) signatureInputRef.current.value = '';
      return;
    }
    setSignatureUploading(true);
    try {
      await userService.uploadSignature(editingUser.id, file);
      const url = await userService.getSignatureUrl(editingUser.id);
      setSignatureUrl(url);
      table.setData(table.data.map(u => u.id === editingUser.id ? { ...u, signature_s3_key: 'uploaded' } : u));
      toast.success('Signature uploaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload signature');
    } finally {
      setSignatureUploading(false);
      if (signatureInputRef.current) signatureInputRef.current.value = '';
    }
  };

  const handleSignatureDelete = async () => {
    if (!editingUser) return;
    try {
      await userService.deleteSignature(editingUser.id);
      setSignatureUrl(null);
      table.setData(table.data.map(u => u.id === editingUser.id ? { ...u, signature_s3_key: null } : u));
      toast.success('Signature removed');
    } catch (err) {
      console.error(err);
      toast.error('Failed to remove signature');
    }
  };

  const handleActivateFromMenu = async () => {
    if (!table.selectedItem) return;
    const userId = table.selectedItem.id;
    try {
      await userService.updateUser(userId, { is_active: true });
      toast.success('User activated successfully');
      table.setData(table.data.map(u => u.id === userId ? { ...u, is_active: true } : u));
    } catch (err) {
      console.error('Error activating user:', err);
      toast.error('Failed to activate user');
    }
    table.handleMenuClose();
  };

  const handleDeactivateFromMenu = async () => {
    if (!table.selectedItem) return;
    const userId = table.selectedItem.id;
    try {
      await userService.updateUser(userId, { is_active: false });
      toast.success('User deactivated successfully');
      table.setData(table.data.map(u => u.id === userId ? { ...u, is_active: false } : u));
    } catch (err) {
      console.error('Error deactivating user:', err);
      toast.error('Failed to deactivate user');
    }
    table.handleMenuClose();
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    const options: ContextMenuOption[] = [
      { label: 'Edit', icon: <PersonIcon sx={{ mr: 2 }} />, onClick: () => { handleNameClick(table.selectedItem!); table.handleMenuClose(); } },
    ];
    if (table.selectedItem.is_active) {
      options.push({ label: 'Deactivate', onClick: () => { handleDeactivateFromMenu(); } });
    } else {
      options.push({ label: 'Activate', onClick: () => { handleActivateFromMenu(); } });
    }
    return options;
  };

  const onSubmit = async (data: UserCreate & UserUpdate & { confirmPassword?: string }) => {
    try {
      const {
        confirmPassword, is_active: _is_active,
        clinic_mode, clinic_ids, ...rest
      } = data as typeof data & { clinic_mode?: ClinicMode; clinic_ids?: number[] };

      // The form asks one question - all, only these, or all except these -
      // and the API takes two lists. "All" is the ABSENCE of rows, so both go
      // empty; it is never stored as a row per clinic.
      const picked = clinic_ids || [];
      const userData = {
        ...rest,
        included_clinic_ids: clinic_mode === 'include' ? picked : [],
        excluded_clinic_ids: clinic_mode === 'exclude' ? picked : [],
      };

      if (!editingUser && data.password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }

      if (editingUser) {
        const { password, ...updateData } = userData;
        const updatedUser = await userService.updateUser(editingUser.id, updateData);
        table.setData(table.data.map(u => u.id === editingUser.id ? updatedUser : u));
        toast.success('User updated successfully');
      } else {
        const newUser = await userService.createUser({ ...userData, role: 'admin', tenant_id: currentUser?.tenant_id } as UserCreate);
        if (pendingSignatureFile) {
          try {
            await userService.uploadSignature(newUser.id, pendingSignatureFile);
            newUser.signature_s3_key = 'uploaded';
          } catch (err) {
            console.error('Failed to upload signature:', err);
            toast.error('User created but signature upload failed');
          }
        }
        table.setData([...table.data, newUser]);
        toast.success('User created successfully');
      }

      setAddDialogOpen(false);
      setEditingUser(null);
      setPendingSignatureFile(null);
      setPendingSignaturePreview(null);
      reset();
    } catch (error: any) {
      console.error('Error saving user:', error);
      toast.error(getApiError(error, 'Failed to save user'));
    }
  };

  // Column value renderer
  const getColumnValue = (user: User, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Typography
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              handleNameClick(user);
            }}
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'primary.main',
              '&:hover': { color: 'primary.dark' }
            }}
          >
            {user.full_name}
          </Typography>
        );
      case 'email': return user.email;
      case 'userType': return formatUserType(user.user_type);
      case 'privilege': return getPrivilegeName(user.privilege_id);
      case 'phone': return user.phone || 'N/A';
      case 'status':
        return (
          <Chip
            label={user.is_active ? 'Active' : 'Inactive'}
            color={user.is_active ? 'success' : 'default'}
            size="small"
          />
        );
      case 'twoFactor':
        return (
          <Chip
            label={user.is_2fa_enabled ? 'Enabled' : 'Disabled'}
            color={user.is_2fa_enabled ? 'success' : 'default'}
            size="small"
          />
        );
      case 'dataAccess': {
        const da = user.data_access;
        const label = da === 'TEST' ? 'Test' : da === 'ALL' ? 'All' : 'Prod';
        const color = da === 'TEST' ? 'warning' : da === 'ALL' ? 'info' : 'default';
        return <Chip label={label} color={color as any} size="small" />;
      }
      case 'licenseNumber': return user.license_number || 'N/A';
      case 'yomanId':
        return user.yoman_id ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{user.yoman_id}</Typography> : 'N/A';
      case 'subClinic': return privilegeHasHmo(user.privilege_id) ? (user.sub_clinic || 'N/A') : 'N/A';
      case 'timezone': return user.timezone;
      case 'createdAt': return formatDate(user.created_at);
      default: return 'N/A';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (user: User) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center" flex={1}>
          <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.5rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
            {user.full_name}
          </Typography>
        </Box>
        <Chip
          label={user.is_active ? 'Active' : 'Inactive'}
          color={user.is_active ? 'success' : 'default'}
          size="small"
        />
      </Box>
      <Box sx={{ pl: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          📧 {user.email}
        </Typography>
        {user.privilege_id && (
          <Typography variant="body2" color="text.secondary">
            🔐 {getPrivilegeName(user.privilege_id)}
          </Typography>
        )}
      </Box>
    </>
  );

  if (!currentUser?.user_privilege?.manage_users) {
    return (
      <Alert severity="error">
        You don't have permission to access this page.
      </Alert>
    );
  }

  const dialogTitle = editingUser
    ? (editingUser.id === currentUser?.id ? 'Edit Your Profile' : 'Edit User')
    : 'Add New User';

  const dialogActions = (
    <>
      <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
        {editingUser ? 'Update' : 'Create'}
      </Button>
      <Button onClick={() => setAddDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>
        Cancel
      </Button>
    </>
  );

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading users:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="User Management"
        searchPlaceholder="Search users by name or email..."
        isAdmin={!!currentUser?.user_privilege?.config}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add User
          </Button>
        }
      />

      {isMobile ? (
        <MobileCardGrid
          data={table.sortedData}
          renderCard={renderMobileCard}
          getItemKey={(u) => u.id}
          onMenuClick={table.handleRowClick}
          emptyIcon={<PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No users found"
        />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            getRowKey={(u) => u.id}
            onRowClick={table.handleRowClick}
          />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      {/* Add/Edit User Dialog */}
      <ClinicDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        title={dialogTitle}
        maxWidth="md"
        onSubmit={handleSubmit(onSubmit)}
        actions={dialogActions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box display="flex" gap={2}>
            <Box sx={{ ...CLINIC_DIALOG_SX.fieldRow, flex: 1 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                First Name
              </Typography>
              <TextField
                {...register('first_name', { required: 'First name is required' })}
                fullWidth
                size="small"
                error={!!errors.first_name}
                helperText={errors.first_name?.message as string}
                InputProps={textFieldInputProps}
              />
            </Box>
            <Box sx={{ ...CLINIC_DIALOG_SX.fieldRow, flex: 1 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                Last Name
              </Typography>
              <TextField
                {...register('last_name', { required: 'Last name is required' })}
                fullWidth
                size="small"
                error={!!errors.last_name}
                helperText={errors.last_name?.message as string}
                InputProps={textFieldInputProps}
              />
            </Box>
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
              Email
            </Typography>
            <TextField
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              fullWidth
              size="small"
              type="email"
              error={!!errors.email}
              helperText={errors.email?.message as string}
              InputProps={textFieldInputProps}
            />
          </Box>

          {!editingUser && (
            <>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                  Password
                </Typography>
                <TextField
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                    maxLength: { value: 72, message: 'Password cannot be longer than 72 characters' }
                  })}
                  fullWidth
                  size="small"
                  type="password"
                  error={!!errors.password}
                  helperText={errors.password?.message as string}
                  InputProps={textFieldInputProps}
                />
              </Box>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                  Confirm
                </Typography>
                <TextField
                  {...register('confirmPassword', { required: 'Please confirm password' })}
                  fullWidth
                  size="small"
                  type="password"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message as string}
                  InputProps={textFieldInputProps}
                />
              </Box>
            </>
          )}

          <Box display="flex" gap={2}>
            <Box sx={{ ...CLINIC_DIALOG_SX.fieldRow, flex: 1 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                User Type
              </Typography>
              <FormControl fullWidth size="small">
                <Controller
                  name="user_type"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} sx={selectSx(!!field.value)}>
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="doctor">Doctor</MenuItem>
                      <MenuItem value="case_manager">Case Manager</MenuItem>
                      <MenuItem value="nurse">Nurse</MenuItem>
                      <MenuItem value="nurse_prescriber">Nurse Prescriber</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Box>
            <Box sx={{ ...CLINIC_DIALOG_SX.fieldRow, flex: 1 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                Privilege
              </Typography>
              <FormControl fullWidth size="small">
                <Controller
                  name="privilege_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      disabled={editingUser?.id === currentUser?.id}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : '')}
                      sx={selectSx(!!field.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {privileges.map((privilege) => (
                        <MenuItem key={privilege.id} value={privilege.id}>
                          {privilege.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Box>
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
              Phone
            </Typography>
            <TextField
              {...register('phone')}
              fullWidth
              size="small"
              InputProps={textFieldInputProps}
            />
          </Box>

          <Box display="flex" gap={2}>
            <Box sx={{ ...CLINIC_DIALOG_SX.fieldRow, flex: 1 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                License #
              </Typography>
              <TextField
                {...register('license_number')}
                fullWidth
                size="small"
                InputProps={textFieldInputProps}
              />
            </Box>
            <Box sx={{ ...CLINIC_DIALOG_SX.fieldRow, flex: 1 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                Yoman ID
              </Typography>
              <TextField
                {...register('yoman_id')}
                fullWidth
                size="small"
                InputProps={textFieldInputProps}
              />
            </Box>
          </Box>

          {privilegeHasHmo(watch('privilege_id')) && (
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                Sub Clinic
              </Typography>
              <TextField
                {...register('sub_clinic')}
                fullWidth
                size="small"
                InputProps={textFieldInputProps}
              />
            </Box>
          )}

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
              Meeting Types
            </Typography>
            <FormControl fullWidth size="small">
              <Controller
                name="meeting_type_ids"
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <Select
                    multiple
                    MenuProps={MULTI_SELECT_MENU_PROPS}
                    value={field.value || []}
                    onChange={(e) => field.onChange(e.target.value as number[])}
                    displayEmpty
                    renderValue={(selected) => {
                      const ids = selected as number[];
                      if (ids.length === 0) return <span style={{ color: '#999' }}>Select meeting types</span>;
                      return ids.map(id => meetingTypes.find(mt => mt.id === id)?.name || id).join(', ');
                    }}
                    sx={selectSx((field.value || []).length > 0)}
                  >
                    {meetingTypes.map((mt) => (
                      <MenuItem key={mt.id} value={mt.id}>
                        {/* The row highlight alone did not say what was
                            selected - with twenty-odd options the list read as
                            a plain menu. */}
                        <Checkbox
                          size="small"
                          checked={(field.value || []).indexOf(mt.id) > -1}
                        />
                        <ListItemText primary={mt.name} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
              Patient Types
            </Typography>
            <FormControl fullWidth size="small">
              <Controller
                name="patient_type_ids"
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <Select
                    multiple
                    MenuProps={MULTI_SELECT_MENU_PROPS}
                    value={field.value || []}
                    onChange={(e) => field.onChange(e.target.value as number[])}
                    displayEmpty
                    renderValue={(selected) => {
                      const ids = selected as number[];
                      // Empty is a real setting, not a missing one, so it says
                      // what it means rather than "Select patient types".
                      if (ids.length === 0) return <span style={{ color: '#999' }}>Adult only (default)</span>;
                      return ids.map(id => patientTypes.find(pt => pt.id === id)?.name || id).join(', ');
                    }}
                    sx={selectSx((field.value || []).length > 0)}
                  >
                    {patientTypes.map((pt) => (
                      <MenuItem key={pt.id} value={pt.id}>
                        <Checkbox
                          size="small"
                          checked={(field.value || []).indexOf(pt.id) > -1}
                        />
                        <ListItemText primary={pt.name} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
              Clinics
            </Typography>
            <FormControl fullWidth size="small">
              <Controller
                name={'clinic_mode' as any}
                control={control}
                defaultValue={'all' as any}
                render={({ field }) => (
                  <Select
                    value={field.value || 'all'}
                    onChange={(e) => {
                      const next = e.target.value as ClinicMode;
                      if (next === field.value) return;
                      field.onChange(next);
                      // Always start the new scope empty. Clinics picked to be
                      // INCLUDED mean the opposite once the mode is EXCLUDE,
                      // and carrying them over silently inverts what someone
                      // chose. Under "All clinics" a leftover list would not
                      // even be on screen, and would resurface on next edit.
                      setValue('clinic_ids' as any, []);
                      // Then open the list they now have to fill in. Deferred
                      // by a tick so the mode menu has finished closing and
                      // handed focus back before the next one takes it.
                      if (next !== 'all') {
                        setTimeout(() => setClinicListOpen(true), 0);
                      }
                    }}
                    sx={selectSx(true)}
                  >
                    {(['all', 'include', 'exclude'] as ClinicMode[]).map((m) => (
                      <MenuItem key={m} value={m}>{CLINIC_MODE_LABEL[m]}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>

          {/* The list only matters once the scope is narrowed, so it is not
              shown - and not asked for - under "All clinics". */}
          {watchedClinicMode !== 'all' && (
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                {watchedClinicMode === 'include' ? 'Only these' : 'Except these'}
              </Typography>
              <FormControl fullWidth size="small">
                <Controller
                  name={'clinic_ids' as any}
                  control={control}
                  defaultValue={[] as any}
                  render={({ field }) => (
                    <Select
                      multiple
                      MenuProps={MULTI_SELECT_MENU_PROPS}
                      open={clinicListOpen}
                      onOpen={() => setClinicListOpen(true)}
                      onClose={() => setClinicListOpen(false)}
                      value={field.value || []}
                      onChange={(e) => field.onChange(e.target.value as number[])}
                      displayEmpty
                      renderValue={(selected) => {
                        const ids = selected as number[];
                        if (ids.length === 0) {
                          return <span style={{ color: '#999' }}>Select clinics</span>;
                        }
                        return ids.map(id => clinics.find(c => c.id === id)?.name || id).join(', ');
                      }}
                      sx={selectSx((field.value || []).length > 0)}
                    >
                      {clinics.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          <Checkbox
                            size="small"
                            checked={(field.value || []).indexOf(c.id) > -1}
                          />
                          <ListItemText primary={c.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Box>
          )}

          {/* The rules compose in ways that are easy to get wrong, so the form
              states the outcome rather than leaving it to be worked out. */}
          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel} />
            <Typography variant="caption" color="text.secondary">
              {clinicScopeSummary(watchedClinicMode, watchedClinicIds, clinics)}
            </Typography>
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
              Accepting New Patients
            </Typography>
            <Controller
              name="accepts_new_patients"
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value ?? true}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      {(field.value ?? true)
                        ? 'Offered for intakes with new patients'
                        : 'Intakes only; still takes follow-ups with existing patients'}
                    </Typography>
                  }
                />
              )}
            />
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
              Data Access
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                {...register('data_access')}
                value={watch('data_access') || 'PROD'}
                sx={selectSx(true)}
              >
                <MenuItem value="PROD">Prod (real patients only)</MenuItem>
                <MenuItem value="TEST">Test (test patients only)</MenuItem>
                <MenuItem value="ALL">All</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
              Timezone
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                {...register('timezone', { required: 'Timezone is required' })}
                value={watch('timezone') || 'Asia/Jerusalem'}
                sx={selectSx(true)}
              >
                <MenuItem value="UTC">UTC</MenuItem>
                <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                <MenuItem value="America/Anchorage">Alaska Time (AKT)</MenuItem>
                <MenuItem value="Pacific/Honolulu">Hawaii Time (HST)</MenuItem>
                <MenuItem value="Europe/London">London (GMT/BST)</MenuItem>
                <MenuItem value="Europe/Paris">Paris (CET/CEST)</MenuItem>
                <MenuItem value="Europe/Berlin">Berlin (CET/CEST)</MenuItem>
                <MenuItem value="Europe/Moscow">Moscow (MSK)</MenuItem>
                <MenuItem value="Asia/Jerusalem">Jerusalem (IST)</MenuItem>
                <MenuItem value="Asia/Dubai">Dubai (GST)</MenuItem>
                <MenuItem value="Asia/Kolkata">India (IST)</MenuItem>
                <MenuItem value="Asia/Shanghai">China (CST)</MenuItem>
                <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
                <MenuItem value="Asia/Seoul">Seoul (KST)</MenuItem>
                <MenuItem value="Australia/Sydney">Sydney (AEDT/AEST)</MenuItem>
                <MenuItem value="Pacific/Auckland">Auckland (NZDT/NZST)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {watch('user_type') === 'doctor' && (
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                Signature
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {signatureLoading ? (
                  <CircularProgress size={24} />
                ) : (signatureUrl || pendingSignaturePreview) ? (
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1, display: 'inline-block', bgcolor: '#fafafa' }}>
                    <img src={signatureUrl || pendingSignaturePreview!} alt="Signature" style={{ maxHeight: 80, maxWidth: 300, display: 'block' }} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No signature uploaded</Typography>
                )}
                <Box display="flex" gap={1}>
                  <input
                    ref={signatureInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleSignatureUpload}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={signatureUploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
                    disabled={signatureUploading}
                    onClick={() => signatureInputRef.current?.click()}
                  >
                    {(signatureUrl || pendingSignaturePreview) ? 'Replace' : 'Upload'}
                  </Button>
                  {(signatureUrl || pendingSignaturePreview) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={editingUser ? handleSignatureDelete : () => { setPendingSignatureFile(null); setPendingSignaturePreview(null); }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </ClinicDialog>

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!currentUser?.user_privilege?.config} />
    </TablePageLayout>
  );
};

export default UsersPage;
